import { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SchedulingData, TimeSlot, TimeSlotVote, RepeatOption } from '../interfaces/interface';
import { SchedulingService } from '../services/SchedulingService';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { NotifyMeetingParticipants } from '../utils/notifyCreator';
import { formatTimeSlotLocalLabel } from 'src/utilities/DateTimeUtilities';
import { logger } from 'src/logger';

interface UseSchedulingProps {
  meetingId: string | null;
  creatorAlias: string | null;
  currentUserAlias: string;
  currentUserName?: string;
  meetingSubject?: string;
}

/**
 * Hook to manage scheduling state: time slots, votes, comments.
 * Loads data from DDB on mount and provides CRUD operations.
 */
export const useScheduling = ({ meetingId, creatorAlias, currentUserAlias, currentUserName, meetingSubject }: UseSchedulingProps) => {
  const [schedulingData, setSchedulingData] = useState<SchedulingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const loadedRef = useRef(false);
  const schedulingDataRef = useRef(schedulingData);
  useEffect(() => {
    schedulingDataRef.current = schedulingData;
  }, [schedulingData]);

  const isCreator = creatorAlias?.toLowerCase() === currentUserAlias.toLowerCase();

  // Load scheduling data on mount
  const loadSchedulingData = useCallback(async () => {
    if (!meetingId || !creatorAlias) return;
    setIsLoading(true);
    try {
      const data = await SchedulingService.getFullSchedulingData(creatorAlias, meetingId);
      setSchedulingData(data);
    } catch (error) {
      logger.error('Failed to load scheduling data', { error, meetingId, creatorAlias });
    } finally {
      setIsLoading(false);
    }
  }, [meetingId, creatorAlias]);

  useEffect(() => {
    if (loadedRef.current || !meetingId || !creatorAlias) return;
    loadedRef.current = true;
    loadSchedulingData();
  }, [meetingId, creatorAlias, loadSchedulingData]);

  // ── Duration & Repeat ────────────────────────────────────────────────

  const updateDuration = useCallback(
    async (duration: number) => {
      if (!meetingId || !creatorAlias || !isCreator) return;
      setSchedulingData((prev) => (prev ? { ...prev, duration } : prev));
      try {
        await EmployeeSettingsService.updateMeetingMetadata(creatorAlias, meetingId, {
          organizerLogin: currentUserAlias,
          duration
        });
      } catch (error) {
        logger.error('Failed to save duration', { error, meetingId });
      }
    },
    [meetingId, creatorAlias, isCreator, currentUserAlias]
  );

  const updateRepeat = useCallback(
    async (repeat: RepeatOption) => {
      if (!meetingId || !creatorAlias || !isCreator) return;
      setSchedulingData((prev) => (prev ? { ...prev, repeat } : prev));
      try {
        await EmployeeSettingsService.updateMeetingMetadata(creatorAlias, meetingId, {
          organizerLogin: currentUserAlias,
          repeat
        });
      } catch (error) {
        logger.error('Failed to save repeat', { error, meetingId });
      }
    },
    [meetingId, creatorAlias, isCreator, currentUserAlias]
  );

  const updateSubject = useCallback(
    async (subject: string) => {
      if (!meetingId || !creatorAlias) return;
      try {
        await EmployeeSettingsService.updateMeetingMetadata(creatorAlias, meetingId, { subject });
      } catch (error) {
        logger.error('Failed to save subject', { error, meetingId });
      }
    },
    [meetingId, creatorAlias]
  );

  // ── Time Slot CRUD ───────────────────────────────────────────────────

  const addTimeSlot = useCallback(
    async (date: string, startTime: string, endTime: string, timezone: string, overrideMeetingId?: string, overrideCreatorAlias?: string) => {
      const effectiveMeetingId = overrideMeetingId ?? meetingId;
      const effectiveCreatorAlias = overrideCreatorAlias ?? creatorAlias;
      if (!effectiveMeetingId || !effectiveCreatorAlias) return;
      const currentSlots = schedulingData?.timeSlots ?? [];
      const newSlot: TimeSlot = {
        slotId: uuidv4(),
        date,
        startTime,
        endTime,
        timezone,
        order: currentSlots.length,
        votes: []
      };

      // Optimistic update
      setSchedulingData((prev) => (prev ? { ...prev, timeSlots: [...prev.timeSlots, newSlot] } : prev));

      setIsSaving(true);
      try {
        await SchedulingService.saveTimeSlot(effectiveCreatorAlias, effectiveMeetingId, newSlot);
      } catch (error) {
        logger.error('Failed to save time slot', { error, meetingId: effectiveMeetingId });
        // Revert on failure
        setSchedulingData((prev) => (prev ? { ...prev, timeSlots: prev.timeSlots.filter((s) => s.slotId !== newSlot.slotId) } : prev));
      } finally {
        setIsSaving(false);
      }
    },
    [meetingId, creatorAlias, isCreator, schedulingData?.timeSlots]
  );

  const deleteTimeSlot = useCallback(
    async (slotId: string) => {
      if (!meetingId || !creatorAlias || !isCreator) return;

      // Optimistic soft-delete
      setSchedulingData((prev) =>
        prev ? { ...prev, timeSlots: prev.timeSlots.map((s) => (s.slotId === slotId ? { ...s, isDeleted: true } : s)) } : prev
      );

      try {
        await SchedulingService.deleteTimeSlot(creatorAlias, meetingId, slotId);
      } catch (error) {
        logger.error('Failed to delete time slot', { error, meetingId, slotId });
        await loadSchedulingData();
      }
    },
    [meetingId, creatorAlias, isCreator, loadSchedulingData]
  );

  const reorderTimeSlots = useCallback(
    async (reorderedSlots: TimeSlot[]) => {
      if (!meetingId || !creatorAlias || !isCreator) return;

      // Optimistic update
      setSchedulingData((prev) => (prev ? { ...prev, timeSlots: reorderedSlots } : prev));

      try {
        await Promise.all(reorderedSlots.map((slot, index) => SchedulingService.updateSlotOrder(creatorAlias, meetingId, slot.slotId, index)));
      } catch (error) {
        logger.error('Failed to reorder time slots', { error, meetingId });
        await loadSchedulingData();
      }
    },
    [meetingId, creatorAlias, isCreator, loadSchedulingData]
  );

  // ── Votes ────────────────────────────────────────────────────────────

  const submitVote = useCallback(
    async (slotId: string, vote: 'up' | 'down') => {
      if (!meetingId || !creatorAlias) return;

      const newVote: TimeSlotVote = {
        loginName: currentUserAlias,
        vote,
        votedAt: new Date().toISOString()
      };

      // Optimistic update
      setSchedulingData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          timeSlots: prev.timeSlots.map((s) => {
            if (s.slotId !== slotId) return s;
            const votes = s.votes.filter((v) => v.loginName !== currentUserAlias);
            votes.push(newVote);
            return { ...s, votes };
          })
        };
      });

      try {
        const success = await SchedulingService.saveVote(creatorAlias, meetingId, slotId, newVote);
        if (!success) {
          await loadSchedulingData();
        } else if (creatorAlias) {
          const slot = schedulingDataRef.current?.timeSlots.find((s) => s.slotId === slotId);
          NotifyMeetingParticipants({
            type: 'vote',
            actorLogin: currentUserAlias,
            actorName: currentUserName || currentUserAlias,
            creatorAlias,
            meetingId,
            meetingSubject: meetingSubject || 'Untitled',
            slotId,
            slotLabel: slot ? formatTimeSlotLocalLabel(slot.date, slot.startTime, slot.endTime, slot.timezone) : undefined,
            voteDirection: vote
          }).catch(() => {});
        }
      } catch (error) {
        logger.error('Failed to save vote', { error, meetingId, slotId });
        await loadSchedulingData();
      }
    },
    [meetingId, creatorAlias, currentUserAlias, currentUserName, meetingSubject, loadSchedulingData]
  );

  // ── Derived State ────────────────────────────────────────────────────

  const activeTimeSlots = (schedulingData?.timeSlots ?? []).filter((s) => !s.isDeleted);

  const result: SchedulingActions = {
    schedulingData,
    isLoading,
    isSaving,
    isCreator,
    activeTimeSlots,
    duration: schedulingData?.duration ?? 30,
    repeat: schedulingData?.repeat ?? 'none',

    // Actions
    loadSchedulingData,
    updateSubject,
    updateDuration,
    updateRepeat,
    addTimeSlot,
    deleteTimeSlot,
    reorderTimeSlots,
    submitVote
  };

  return result;
};

/** The return type of useScheduling — used as a grouped prop by SchedulingContainer */
export interface SchedulingActions {
  schedulingData: SchedulingData | null;
  isLoading: boolean;
  isSaving: boolean;
  isCreator: boolean;
  activeTimeSlots: TimeSlot[];
  duration: number;
  repeat: RepeatOption;
  loadSchedulingData: () => Promise<void>;
  updateSubject: (subject: string) => Promise<void>;
  updateDuration: (duration: number) => Promise<void>;
  updateRepeat: (repeat: RepeatOption) => Promise<void>;
  addTimeSlot: (
    date: string,
    startTime: string,
    endTime: string,
    timezone: string,
    overrideMeetingId?: string,
    overrideCreatorAlias?: string
  ) => Promise<void>;
  deleteTimeSlot: (slotId: string) => Promise<void>;
  reorderTimeSlots: (slots: TimeSlot[]) => Promise<void>;
  submitVote: (slotId: string, vote: 'up' | 'down') => Promise<void>;
}
